import { CommonModule, DatePipe } from '@angular/common';
import { Component, DestroyRef, computed, effect, inject, OnInit, Signal, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { IntelligenceModels } from '@models/intelligence.model';
import { IntelligenceResultsService } from '../../../../../../services/intelligence-results.service';
import { routerSegments } from '@consts/routes';
import {
  ConfirmDialogModel,
  ConfirmDialogService,
  PreloaderColor,
  UiButtonComponent,
  UiFilterChipComponent,
  UiPreloaderComponent,
} from '@ui';
import { CaseManagementService } from '../../../../../../services/case-management.service';
import { UtilsService } from '../../../../../../edge/utils.service';
import { CaseManagementModel } from '@models/case-management.model';
import { catchError, debounceTime, of, take } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTooltip } from '@angular/material/tooltip';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { AppState } from '../../../../../../store/app.state';
import { CaseManagementActions } from '@states/case-management/case-management.actions';
import { caseManagementFeature } from '@states/case-management/case-management.feature';
import { SharedActions } from '@states/shared/shared.action-types';
import { CaseCreateDialogComponent } from '../../../../../../pages/case-management/dialogs/case-create-dialog/case-create-dialog.component';
import { IntelligenceToolsService } from '../../../../../../services/intelligence-tools.service';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatOption, MatSelect, MatSelectChange } from '@angular/material/select';
import { TagFilterComponent } from '../../../../../../pages/database/pages/tags/components/tag-filter/tag-filter.component';
import { DatabaseTagsSelectors } from '@states/database-tags/database-tags.selector-types';
import { DatabaseTagsActions } from '@states/database-tags/database-tags.action.types';
import { UiSearchComponent } from '../../../../../ui-kit/ui-search/ui-search.component';

const CASE_DIALOG_CONFIG = {
  width: '600px',
  panelClass: 'modal-no-padding',
  height: '70vh',
  disableClose: true,
} as const;

@Component({
  selector: 'app-case-manager-result',
  standalone: true,
  imports: [
    CommonModule,
    DatePipe,
    TranslatePipe,
    UiButtonComponent,
    UiFilterChipComponent,
    UiPreloaderComponent,
    UiSearchComponent,
    MatTooltip,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatSelect,
    MatOption,
    ReactiveFormsModule,
    TagFilterComponent,
  ],
  templateUrl: './case-manager-result.component.html',
  styleUrls: ['./case-manager-result.component.scss'],
})
export class CaseManagerResultComponent implements OnInit {
  private readonly resultsService = inject(IntelligenceResultsService);
  private readonly toolsService = inject(IntelligenceToolsService);
  private readonly router = inject(Router);
  private readonly caseManagementService = inject(CaseManagementService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translateService = inject(TranslateService);
  private readonly dialog = inject(MatDialog);
  private readonly store$ = inject<Store<AppState>>(Store);
  private readonly actions$: Actions = inject(Actions);
  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly utilsService = inject(UtilsService);
  private readonly selectedCase: Signal<CaseManagementModel.CaseDocument | null> =
    this.store$.selectSignal(caseManagementFeature.selectSelectedCase);

  protected readonly PreloaderColor = PreloaderColor;
  protected readonly caseStatuses = Object.values(CaseManagementModel.CaseStatus);
  protected readonly caseStatusDisplayMap = CaseManagementModel.CASE_STATUS_DISPLAY_MAP;

  protected readonly searchQuery = signal<string>('');
  protected readonly selectedStatuses = signal<CaseManagementModel.CaseStatus[]>([]);
  protected readonly selectedTags = signal<string[]>([]);
  protected readonly tagsFilterControl = new FormControl<string[]>([], { nonNullable: true });
  protected readonly isFiltering = signal<boolean>(false);
  protected readonly tagEntities = this.store$.selectSignal(
    DatabaseTagsSelectors.selectDatabaseTagEntities,
  );
  protected readonly hasActiveFilters = computed<boolean>(
    () =>
      !!this.searchQuery() ||
      this.selectedStatuses().length > 0 ||
      this.selectedTags().length > 0,
  );

  readonly expandedCaseId = signal<string | null>(null);
  protected readonly deletingCaseId = signal<string | null>(null);
  protected readonly countersByCaseId = signal<Record<string, CaseManagementModel.CaseFeatureCounters>>({});
  protected readonly countersLoadingByCaseId = signal<Record<string, boolean>>({});
  protected readonly featureItems: IntelligenceModels.CaseManager.FeatureItem[] = [
    { id: 'clips', labelKey: 'CASES.VIEW.CLIPS' },
    { id: 'objects', labelKey: 'CASES.VIEW.OBJECTS' },
    { id: 'attachments', labelKey: 'CASES.VIEW.EXTERNAL_FILES' },
    { id: 'accessControlEvents', labelKey: 'CASES.VIEW.ACCESS_CONTROL_EVENTS' },
  ];
  private readonly emptyCounters: CaseManagementModel.CaseFeatureCounters = {
    attachments: 0,
    fileRequests: 0,
    clips: 0,
    objects: 0,
    accessControlEvents: 0,
  };

  readonly response = computed<IntelligenceModels.CaseManager.Response | null>(() => {
    const result = this.resultsService.current();
    if (result?.type !== 'case-manager') return null;
    return result.data as IntelligenceModels.CaseManager.Response;
  });

  readonly cases = computed(() => this.response()?.cases ?? []);

  private lastEntryPosition = this.resultsService.currentPosition();
  private filterRequestId = 0;
  private readonly hydrateOnNavigation = effect(() => {
    const position = this.resultsService.currentPosition();
    if (position === this.lastEntryPosition) return;
    this.lastEntryPosition = position;
    this.filterRequestId += 1;
    this.isFiltering.set(false);
    this.hydrateFilters(this.response()?.filter);
  });

  constructor() {
    // The dialog closes with no payload on update, so the updated case comes from the
    // action. Component-scoped (not dialog-scoped) so an edit made anywhere — including
    // the case-view page under the palette — refreshes a row we happen to be showing;
    // updateCaseInCurrent no-ops when the case isn't in the current result.
    this.actions$
      .pipe(ofType(CaseManagementActions.updateCaseSuccess), takeUntilDestroyed(this.destroyRef))
      .subscribe(({ updatedCase }) => this.resultsService.updateCaseInCurrent(updatedCase));

    // The dialog renders its own Delete button in edit mode only, so this is the first
    // path by which a case can be deleted from the palette without going through
    // deleteCase() above. removeCaseFromCurrent no-ops when the row is already gone.
    this.actions$
      .pipe(ofType(CaseManagementActions.deleteCaseSuccess), takeUntilDestroyed(this.destroyRef))
      .subscribe(({ id }) => this.resultsService.removeCaseFromCurrent(id));
  }

  public ngOnInit(): void {
    if (!Object.keys(this.tagEntities()).length) {
      this.store$.dispatch(DatabaseTagsActions.getDatabaseTags());
    }

    this.hydrateFilters(this.response()?.filter);
    this.tagsFilterControl.valueChanges
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe((tags) => {
        this.selectedTags.set(tags);
        void this.reload();
      });
  }

  protected onSearchQuery(value: string): void {
    const next = value ?? '';
    if (next === this.searchQuery()) return;
    this.searchQuery.set(next);
    void this.reload();
  }

  protected onStatusChange(event: MatSelectChange): void {
    this.selectedStatuses.set(event.value ?? []);
    void this.reload();
  }

  protected removeSearch(): void {
    this.searchQuery.set('');
    void this.reload();
  }

  protected removeStatus(status: CaseManagementModel.CaseStatus): void {
    this.selectedStatuses.update((statuses) =>
      statuses.filter((selectedStatus) => selectedStatus !== status),
    );
    void this.reload();
  }

  protected removeTag(tagId: string): void {
    this.tagsFilterControl.setValue(
      this.tagsFilterControl.value.filter((selectedTagId) => selectedTagId !== tagId),
    );
  }

  protected clearFilters(): void {
    this.searchQuery.set('');
    this.selectedStatuses.set([]);
    this.selectedTags.set([]);
    this.tagsFilterControl.setValue([], { emitEvent: false });
    void this.reload();
  }

  protected createCase(): void {
    this.openCaseDialog(null)
      .afterClosed()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((newCase?: CaseManagementModel.CaseDocument) => {
        if (!newCase?._id) return;
        // CaseDocument and CaseManager.Case (CaseResponseDto) are structurally
        // compatible for the fields the panel reads; cast through unknown.
        this.resultsService.prependCaseToCurrent(
          newCase as unknown as IntelligenceModels.CaseManager.Case,
        );
      });
  }

  /** Open the shared case dialog in edit mode, pre-seeded from the row so it neither refetches nor flashes a spinner. */
  protected editCase(item: IntelligenceModels.CaseManager.Case): void {
    this.openCaseDialog(item, { caseId: item._id });
  }

  toggleExpand(caseId: string): void {
    this.expandedCaseId.update((id) => {
      const nextId = id === caseId ? null : caseId;
      if (nextId) {
        this.loadCounters(nextId);
      }
      return nextId;
    });
  }

  ownerName(item: IntelligenceModels.CaseManager.Case): string {
    const user = item.createdByUser;
    const fullName = [user?.firstname, user?.lastname].filter(Boolean).join(' ');
    return fullName || user?.email || '-';
  }

  incidentNumber(item: IntelligenceModels.CaseManager.Case): string {
    return item.internalIncidentNumber ? `#${item.internalIncidentNumber}` : '#-';
  }

  /** Open the case info page (`/cases/view/:id`) in a new browser tab (mirrors openCaseList). */
  protected goToCase(item: IntelligenceModels.CaseManager.Case): void {
    const tree = this.router.createUrlTree(['/', routerSegments.cases, routerSegments.view, item._id]);
    const path = this.router.serializeUrl(tree);
    const url = window.location?.origin ? `${window.location.origin}${path}` : path;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  /** Confirm, then delete the case; swaps the row's menu icon for an inline loader while in flight. */
  protected deleteCase(item: IntelligenceModels.CaseManager.Case): void {
    if (this.deletingCaseId()) return;
    this.confirmDialog
      .open({
        // ui-confirm-dialog translates these via the `translate` pipe, so pass raw keys.
        type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
        title: 'CASES.DELETE.TITLE',
        msg: 'CASES.DELETE.MESSAGE',
        confirm: 'CONFIRM_MODALS.COMMON_YES',
        disableClose: true,
      })
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        if (res?.selection !== ConfirmDialogModel.ConfirmResult.OK) return;
        this.deletingCaseId.set(item._id);
        this.caseManagementService
          .delete(item._id)
          .pipe(take(1), takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.resultsService.removeCaseFromCurrent(item._id);
              this.deletingCaseId.set(null);
            },
            error: (err) => {
              this.deletingCaseId.set(null);
              this.store$.dispatch(
                SharedActions.showMessage({ error: this.utilsService.errMessage(err) }),
              );
            },
          });
      });
  }

  /** Open the full case list (`/cases/list`) in a new browser tab (mirrors openGroupPage). */
  protected openCaseList(): void {
    const tree = this.router.createUrlTree(['/', routerSegments.cases, routerSegments.list]);
    const path = this.router.serializeUrl(tree);
    const url = window.location?.origin ? `${window.location.origin}${path}` : path;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  protected featureCount(
    caseId: string,
    feature: IntelligenceModels.CaseManager.FeatureType,
  ): number {
    return this.countersByCaseId()[caseId]?.[feature] ?? 0;
  }

  protected isFeatureDisabled(
    item: IntelligenceModels.CaseManager.Case,
    feature: IntelligenceModels.CaseManager.FeatureType,
  ): boolean {
    if (this.featureCount(item._id, feature) > 0) return false;
    // Clips, objects and external files can be opened empty by editors, to add the
    // first item (clips/objects via the AI archive list, files via upload).
    const addable: IntelligenceModels.CaseManager.FeatureType[] = ['attachments', 'clips', 'objects'];
    return !(addable.includes(feature) && this.canEditCase(item));
  }

  protected canEditCase(item: IntelligenceModels.CaseManager.Case): boolean {
    const permission = item.userPermission;
    return permission != null && permission >= CaseManagementModel.CaseUserPermission.EDIT;
  }

  protected openFeature(
    item: IntelligenceModels.CaseManager.Case,
    feature: IntelligenceModels.CaseManager.FeatureType,
  ): void {
    if (this.isFeatureDisabled(item, feature)) {
      return;
    }

    const featureLabel = this.translateService.instant(
      this.featureItems.find((featureItem) => featureItem.id === feature)?.labelKey ?? '',
    );
    this.resultsService.setOverlay(
      {
        caseId: item._id,
        caseName: item.name,
        feature,
        userPermission: item.userPermission,
      },
      'case-manager-feature',
      `${item.name} · ${featureLabel}`,
    );
  }

  trackByCaseId(_index: number, item: IntelligenceModels.CaseManager.Case): string {
    return item._id;
  }

  private loadCounters(caseId: string): void {
    if (this.countersByCaseId()[caseId] || this.countersLoadingByCaseId()[caseId]) {
      return;
    }

    this.countersLoadingByCaseId.update((state) => ({ ...state, [caseId]: true }));
    this.caseManagementService.getFeatureCounters(caseId)
      .pipe(
        take(1),
        catchError(() => of(this.emptyCounters)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((counters) => {
        this.countersByCaseId.update((state) => ({ ...state, [caseId]: counters }));
        this.countersLoadingByCaseId.update((state) => {
          const { [caseId]: _loading, ...rest } = state;
          return rest;
        });
      });
  }

  private openCaseDialog(
    selectedCase: CaseManagementModel.CaseDocument | null,
    data: CaseManagementModel.CreateCaseDialogOptions = {},
  ): MatDialogRef<CaseCreateDialogComponent> {
    const previousSelectedCase: CaseManagementModel.CaseDocument | null = this.selectedCase();
    this.store$.dispatch(CaseManagementActions.setSelectedCase({ selectedCase }));
    const dialogRef: MatDialogRef<CaseCreateDialogComponent> = this.dialog.open(CaseCreateDialogComponent, {
      ...CASE_DIALOG_CONFIG,
      data,
    });
    dialogRef
      .afterClosed()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.store$.dispatch(CaseManagementActions.setSelectedCase({ selectedCase: previousSelectedCase }));
      });
    return dialogRef;
  }

  private hydrateFilters(filter?: IntelligenceModels.CaseManager.Filter): void {
    this.searchQuery.set(filter?.query ?? '');
    this.selectedStatuses.set(filter?.statuses ?? []);
    this.selectedTags.set(filter?.tags ?? []);
    this.tagsFilterControl.setValue(filter?.tags ?? [], { emitEvent: false });
  }

  private async reload(): Promise<void> {
    const entryPosition = this.resultsService.currentPosition();
    const requestId = ++this.filterRequestId;
    const params: IntelligenceModels.CaseManager.ListParams = {
      limit: this.response()?.filter.limit,
      query: this.searchQuery() || undefined,
      statuses: this.selectedStatuses().length ? this.selectedStatuses() : undefined,
      tags: this.selectedTags().length ? this.selectedTags() : undefined,
    };

    this.isFiltering.set(true);
    try {
      const response = await this.toolsService.fetchCaseManager(params);
      if (
        requestId !== this.filterRequestId ||
        entryPosition !== this.resultsService.currentPosition() ||
        this.resultsService.current()?.type !== 'case-manager'
      ) {
        return;
      }
      this.resultsService.replaceCaseManagerInCurrent(response);
    } catch (err) {
      if (
        requestId === this.filterRequestId &&
        entryPosition === this.resultsService.currentPosition()
      ) {
        this.store$.dispatch(
          SharedActions.showMessage({ error: this.utilsService.errMessage(err as Error) }),
        );
      }
    } finally {
      if (requestId === this.filterRequestId) {
        this.isFiltering.set(false);
      }
    }
  }
}
